import { useContext, useEffect } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Contact from "./Pages/Contact";
import AboutUs from "./Pages/AboutUs";
import Register from "./Pages/Register";
import Footer from "./components/Footer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Login from "./Pages/Login";
import { AuthContext } from "./Context/AuthContext";
import Profile from "./Pages/Profile";
import NotificationPage from "./Pages/Notifications";
import FriendsListPage from "./Pages/FriendListPage";
import UserInformationPage from "./Pages/UserInformationPage";
import MainDashboard from "./Pages/Dashboard/MainDashboard";
import ShareData from "./Pages/Dashboard/ShareData";
import AnswersDashboard from "./Pages/Dashboard/Answers";
import QuestionsAndAnswers from "./Pages/Dashboard/QuestionsAndAnswers";
import AppointmentsDashboard from "./Pages/Dashboard/AppointmentsDashboard";
import GetDetails from "./Pages/GetDetails";
import VisualData from "./Pages/VisualData/VisualData";
import VisualDataImage from "./Pages/VisualData/VisualDataImage";
import VisualDataVideo from "./Pages/VisualData/VisualDataVideo";
import AppointmentDetail from "./Pages/Dashboard/AppointmentDetail";
import ShareDataPage from "./Pages/Dashboard/SharePage";
import NurseChat from "./Pages/VisualData/NurseChat";
import AIAgentWrapper from "./components/ElevenLabs/AIAgentWrapper";
import AIAgentDemo from "./components/ElevenLabs/AIAgentDemo";
import NurseChatSetup from "./Pages/VisualData/NurseChatSetup";
import NurseChatAnswers from "./Pages/VisualData/NurseChatAnswers";
import NurseChatAssessments from "./Pages/VisualData/NurseChatAssessments";
import ForgotPassword from "./Pages/Auth/ForgotPassword";
import ResetPassword from "./Pages/Auth/ResetPassword";
import NotFound from "./Pages/NotFound"; // Import the new NotFound page
import VerifyOtp from "./Pages/VerifyOtp";
import DepartmentSignup from "./Pages/DepartmentSignup";

import VisualAnalysisMyRequests from "./Pages/VisualData/VisualDataMyRequests";
import EditProfile from "./Pages/Profile/EditProfile";
import HipaaCompliancePage from "./Pages/Settings/HippaCompliance";
import TermsPage from "./Pages/Settings/Terms";
import HowToUseAISentinelPage from "./Pages/Settings/HowToUseAISentinelPage";
import FaqPage from "./Pages/Settings/FAQ";
import PrivacyPage from "./Pages/Settings/Privacy";
import DoctorsListPage from "./Pages/DoctorsListPage";
import InsuranceListPage from "./Pages/InsuranceListPage";
import SettingsPage from "./Pages/Settings";
import Location from "./Pages/Location";
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { decodeToken } from 'react-jwt';
import SSOOnboarding from "./Pages/SSOOnboarding";
import AIAgentSurg360 from "./components/ElevenLabs/AIAgentSurg360";
import PainManagement from "./Pages/PainManagement";
import Vitals from "./Pages/Vitals";
import Medications from "./Pages/Medications";
import AnesthesiaAIAgent from "./components/ElevenLabs/AnesthesiaAIAgent";
import KannadaAI from "./components/ElevenLabs/KannadaAI";
import FAQAIAgentAll from "./components/ElevenLabs/FAQAIAgentAll";
import SsaiAgent from "./components/ElevenLabs/SsaiAgent";
import PatientRegistrationAIAgent from "./components/ElevenLabs/PatientRegistrationAIAgent";
import AIAgentPage from "./Pages/AIAgentPage";
import AIConversationHistory from "./Pages/AIConversationHistory";
import SignupAIAgent from "./Pages/SignupAIAgent";

const App = () => {
  const { setUser, setIsAuthenticated, setLoading } = useContext(AuthContext);

  // Add a function to verify token validity
  const verifyTokenExpiration = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('dToken');
    if (!token) return false;
    
    try {
      // Decode token to check expiration
      const payload = decodeToken(token);
      if (!payload || !payload.exp) return false;
      
      // Check if token is expired
      const currentTime = Date.now() / 1000;
      if (payload.exp < currentTime) {
        console.log("Token expired, logging out user");
        // Clear the tokens
        localStorage.removeItem('token');
        localStorage.removeItem('dToken');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error verifying token:", error);
      return false;
    }
  };

  // Update useEffect that handles authentication
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Check if setLoading function exists
      if (typeof setLoading === 'function') {
        setLoading(true);
      }
      
      // First check token expiration
      const isTokenValid = verifyTokenExpiration();
      
      if (!isTokenValid) {
        if (typeof setLoading === 'function') {
          setLoading(false);
        }
        return;
      }
      
      try {
        // Fix token retrieval - use consistent naming
        const token = localStorage.getItem('token') || localStorage.getItem('dToken');
        
        if (token) {
          // Use the correct API endpoint that matches the backend route
          const response = await axios.get(
            `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/user/me`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log("API Response:", response.data);
          
          if (response.data.success) {
            if (typeof setUser === 'function') {
              setUser(response.data.user);
            }
            if (typeof setIsAuthenticated === 'function') {
              setIsAuthenticated(true);
            }
          } else {
            // Handle unsuccessful but valid response
            if (typeof setIsAuthenticated === 'function') {
              setIsAuthenticated(false);
            }
            if (typeof setUser === 'function') {
              setUser(null);
            }
            // Clear tokens on error
            localStorage.removeItem('token');
            localStorage.removeItem('dToken');
          }
        }
      } catch (error) {
        console.error("Auth check error:", error);
        if (typeof setIsAuthenticated === 'function') {
          setIsAuthenticated(false);
        }
        if (typeof setUser === 'function') {
          setUser(null);
        }
        
        // Don't clear tokens on network errors to prevent logout on temporary issues
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          localStorage.removeItem('token');
          localStorage.removeItem('dToken');
        }
      } finally {
        if (typeof setLoading === 'function') {
          setLoading(false);
        }
      }
    };
    
    checkAuthStatus();
  }, [setUser, setIsAuthenticated, setLoading]);

  return (
    <Router>
      <ScrollToTop /> {/* Add this line right after Router */}
      <Routes>
        <Route path="/" element={<MainDashboard />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/get-details" element={<GetDetails />} />
        <Route path="/sso-onboarding" element={<SSOOnboarding />} />
        <Route path="/verify-otp" element={<VerifyOtp />} />
        {/* Department QR-code signup (public; token resolved server-side) */}
        <Route path="/signup/:qrToken" element={<DepartmentSignup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignupAIAgent />} />
        {/* New Pages */}
        <Route path="/profile"  element={<Profile />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute>
              <NotificationPage />
            </ProtectedRoute>
          } 
        />
        <Route path="/friends-list" element={<FriendsListPage />} />
        <Route path="/edit-profile" element={<EditProfile />} />
        <Route path="/user-information" element={<UserInformationPage />} />
        <Route path="/dashboard" element={<MainDashboard />} />
        <Route path="/patient/dashboard" element={<MainDashboard />} />
        <Route path="/dashboard/share-data" element={<ShareData />} />
        <Route path="/pain-management" element={<PainManagement />} />
        <Route path="/vitals" element={<Vitals />} />
        <Route path="/medications" element={<Medications />} />
        <Route path="/dashboard/share-data/answers" element={<AnswersDashboard />} />
        <Route path="/dashboard/share-data/answers/:id" element={<QuestionsAndAnswers />} />
        <Route path="/dashboard/share-data/list" element={<ShareDataPage />} />
        <Route path="/appointments" element={<AppointmentsDashboard />} />
        <Route path="/visual-data" element={<VisualData />} />
        <Route path="/visual-data/create-request" element={<VisualDataImage />} />
        <Route path="/visual-data/video" element={<VisualDataVideo />} />
        <Route path="/visual-data/my-requests" element={<VisualAnalysisMyRequests />} />
        <Route path="/appointment/:appointmentId" element={<AppointmentDetail />} />
        <Route
          path="/appointment/confirm/:meetingId"
          element={<AppointmentDetail />}
        />

        <Route path="/dashboard/nurse-chat" element={<NurseChat />} />
        <Route path="/dashboard/nurse-chat/setup" element={<NurseChatSetup />} />
        <Route path="/dashboard/nurse-chat/answers" element={<NurseChatAnswers />} />
        <Route path="/dashboard/nurse-chat/assessments" element={<NurseChatAssessments />} />
        <Route path="/dashboard/nurse-chat/answers/:id" element={<QuestionsAndAnswers />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
        <Route path="/hippa-compliance" element={<HipaaCompliancePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/how" element={<HowToUseAISentinelPage />} />
        <Route path="/privacy" element={<PrivacyPage   />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/doctors-list" element={<DoctorsListPage />} />
        <Route path="/insurance-list" element={<InsuranceListPage />} />
        <Route path="/location" element={<Location />} />
        <Route path="/appointments" element={<AppointmentsDashboard />} />
        <Route 
          path="/ai-agent" 
          element={
            <ProtectedRoute>
              <AIAgentWrapper />
            </ProtectedRoute>
          } 
        />
        <Route path="/kannada-ai" element={<ProtectedRoute><KannadaAI /></ProtectedRoute>} />
        <Route 
          path="/ai-agent-surg360" 
          element={
            <ProtectedRoute>
              <AIAgentSurg360 />
            </ProtectedRoute>
          } 
        />

        

        <Route 
          path="/ssai-agent" 
          element={
            <ProtectedRoute>
              <SsaiAgent />
            </ProtectedRoute>
          } 
        />

        
        <Route 
          path="/anesthesia-agent" 
          element={
            <ProtectedRoute>
              <AnesthesiaAIAgent />
            </ProtectedRoute>
          } 
        />


        <Route
          path="/patient-registration"
          element={
            <ProtectedRoute>
              <PatientRegistrationAIAgent />
            </ProtectedRoute>
          }
        />

        <Route
          path="/faq-agent"
          element={
            <ProtectedRoute>
              <FAQAIAgentAll />
            </ProtectedRoute>
          } 
        />

        
        <Route 
          path="/flo" 
          element={
            <ProtectedRoute>
              <AnesthesiaAIAgent />
            </ProtectedRoute>
          } 
        />
        <Route path="/ai-agent-demo" element={<AIAgentDemo />} />
        <Route
          path="/ai-assistant"
          element={
            <ProtectedRoute>
              <AIAgentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-conversations"
          element={
            <ProtectedRoute>
              <AIConversationHistory />
            </ProtectedRoute>
          }
        />
        <Route path="/sso-onboarding" element={<SSOOnboarding />} />
        
        {/* 404 Page - Catch all undefined routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <FooterRouteAware />
      <ToastContainer position="top-center" />
    </Router>
  );
};

// Footer wrapper that hides footer on specific routes
const FooterRouteAware = () => {
  const location = useLocation();
  const hideOnPaths = new Set([
    "/anesthesia-agent",
    "/flo",
    "/kannada-ai",
    "/faq-agent",
    "/ai-agent-surg360",
    "/ai-assistant",
    "/signup",
  ]);
  if (hideOnPaths.has(location.pathname)) return null;
  return <Footer />;
};

export default App;
